import { useState, useRef, useEffect } from 'react';
import {
  Stack, Text, Group, Button, Box, AspectRatio, Image, Loader, Progress, Modal,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { FileButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useQueryClient } from '@tanstack/react-query';
import { IconPhoto, IconUpload, IconCamera, IconTrash } from '@tabler/icons-react';
import { MediaStatusType } from '@lib2/model/media/MediaTypes';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useSharedLinkConfig } from '@/features/link/useLinkConfig';
import { LinkUtils } from '@/features/link/LinkUtils';
import { MediaUtils } from '../MediaUtils';
import { useUploadPoster, useDeletePoster } from '../usePosterMutations';
import { usePosterJobPolling } from '../usePosterJobPolling';
import { CaptureFrameForm } from './CaptureFrameForm';
import {
  MEDIA_QUERY_KEYS,
  POSTER_ACCEPTED_MIME_TYPES,
  POSTER_ACCEPTED_EXTENSIONS,
  POSTER_MAX_FILE_SIZE,
  POSTER_PROCESSING_TIMEOUT_MS,
} from '@/lib/constants/media';
import type { VideoViewData } from '@/lib/types/Media';

interface ImagesTabProps {
  video: VideoViewData;
}

export function ImagesTab({ video }: ImagesTabProps) {
  const mediaId = video.media.mediaId;
  const { selectedCId } = useAuth();
  const { data: linkConfig } = useSharedLinkConfig();
  const queryClient = useQueryClient();

  const uploadPoster = useUploadPoster(mediaId);
  const deletePoster = useDeletePoster(mediaId);

  const [captureOpened, { open: openCaptureForm, close: closeCaptureForm }] = useDisclosure(false);

  // Processing state — initialized from server data for reload-safety
  const isServerProcessing = MediaUtils.hasPendingPosterFile(video.files);
  const [isProcessing, setIsProcessing] = useState(isServerProcessing);
  const [initiatedLocally, setInitiatedLocally] = useState(false);
  const [clientPreviewUrl, setClientPreviewUrl] = useState<string | null>(null);
  const ingestJobIdRef = useRef<number | null>(null);
  const hasNotifiedRef = useRef(false);
  const previousPosterFileIdRef = useRef<number | null>(null);

  const posterStatus = usePosterJobPolling(mediaId, isProcessing, ingestJobIdRef.current);

  const posterFile = MediaUtils.getPosterFile(video.files);
  const posterUrl = posterFile && linkConfig
    ? LinkUtils.getMediaFileUrl(linkConfig, mediaId, posterFile.fileId, posterFile.format)
    : null;
  const hasPoster = posterFile != null;
  const anyMutationPending = uploadPoster.isPending || deletePoster.isPending;

  // Sync effect for external changes (e.g. background refetch detects new pending poster)
  useEffect(() => {
    if (MediaUtils.hasPendingPosterFile(video.files) && !isProcessing) {
      previousPosterFileIdRef.current = posterFile?.fileId ?? null;
      setIsProcessing(true);
      setInitiatedLocally(false);
      ingestJobIdRef.current = null;
      hasNotifiedRef.current = false;
    }
  }, [video.files, isProcessing, posterFile?.fileId]);

  // React to poster job status changes
  // On 'complete': DON'T exit processing — just invalidate queries.
  // The poster-change detection effect below will exit processing once
  // the refetched video.files contains a new Ready poster.
  useEffect(() => {
    if (!posterStatus) return;

    if (posterStatus.phase === 'complete') {
      ingestJobIdRef.current = null;
      if (selectedCId) {
        queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.video(selectedCId, mediaId) });
        queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.videos(selectedCId) });
      }
    }

    if (posterStatus.phase === 'failed' && !hasNotifiedRef.current) {
      setIsProcessing(false);
      ingestJobIdRef.current = null;
      hasNotifiedRef.current = true;

      if (initiatedLocally) {
        notifications.show({
          title: 'Poster processing failed',
          message: 'The poster image could not be processed. Please try again.',
          color: 'red',
        });
      }
    }
  }, [posterStatus, selectedCId, mediaId, queryClient, initiatedLocally]);

  // Poster-change detection: exit processing only when video.files contains
  // a Ready poster with a different fileId than the one before upload started.
  // This prevents the old poster from flashing when the job completes but
  // the backend hasn't fully propagated the new file yet.
  useEffect(() => {
    if (!isProcessing) return;
    const readyPoster = MediaUtils.getPosterFile(video.files);
    if (!readyPoster) return;

    if (readyPoster.fileId !== previousPosterFileIdRef.current && !hasNotifiedRef.current) {
      setIsProcessing(false);
      previousPosterFileIdRef.current = null;
      ingestJobIdRef.current = null;
      hasNotifiedRef.current = true;

      if (selectedCId) {
        queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.videos(selectedCId) });
      }

      if (initiatedLocally) {
        notifications.show({
          title: 'Poster image ready',
          message: 'Your poster image has been processed successfully.',
          color: 'green',
        });
      }
    }
  }, [video.files, isProcessing, selectedCId, queryClient, initiatedLocally]);

  // Safety timeout: prevent permanent spinner
  useEffect(() => {
    if (!isProcessing) return;
    const timer = setTimeout(() => {
      setIsProcessing(false);
      if (selectedCId) {
        queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.video(selectedCId, mediaId) });
        queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.videos(selectedCId) });
      }
    }, POSTER_PROCESSING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isProcessing, selectedCId, mediaId, queryClient]);

  // Cleanup client preview URL on unmount
  useEffect(() => {
    return () => {
      if (clientPreviewUrl) URL.revokeObjectURL(clientPreviewUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Validate and check HEIC/HEIF extension fallback */
  function isAcceptedFile(file: File): boolean {
    const mimeTypes: readonly string[] = POSTER_ACCEPTED_MIME_TYPES;
    if (file.type && mimeTypes.includes(file.type)) return true;
    // Extension fallback for HEIC/HEIF (browsers may report empty MIME)
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    return POSTER_ACCEPTED_EXTENSIONS.includes(ext as typeof POSTER_ACCEPTED_EXTENSIONS[number]);
  }

  async function handleUploadFile(file: File) {
    if (!isAcceptedFile(file)) {
      notifications.show({
        title: 'Invalid file type',
        message: 'Please upload a JPG, PNG, WebP, or HEIC/HEIF image.',
        color: 'red',
      });
      return;
    }

    if (file.size > POSTER_MAX_FILE_SIZE) {
      notifications.show({
        title: 'File too large',
        message: 'Poster image must be under 20MB.',
        color: 'red',
      });
      return;
    }

    // Snapshot current poster so change-detection knows when a new one arrives
    previousPosterFileIdRef.current = posterFile?.fileId ?? null;

    // Set client preview
    const previewUrl = URL.createObjectURL(file);
    setClientPreviewUrl(previewUrl);

    const contentType = file.type || 'application/octet-stream';

    try {
      const ingestJobId = await uploadPoster.mutateAsync({ file, contentType });
      ingestJobIdRef.current = ingestJobId;
      setIsProcessing(true);
      setInitiatedLocally(true);
      hasNotifiedRef.current = false;
    } catch (err) {
      notifications.show({
        title: 'Upload failed',
        message: err instanceof Error ? err.message : 'Failed to upload poster image.',
        color: 'red',
      });
    } finally {
      URL.revokeObjectURL(previewUrl);
      setClientPreviewUrl(null);
    }
  }

  function handleDropzoneDrop(files: File[]) {
    if (files[0]) handleUploadFile(files[0]);
  }

  function handleDropzoneReject() {
    notifications.show({
      title: 'Invalid file',
      message: 'Please upload a valid image file (JPG, PNG, WebP, HEIC/HEIF) under 20MB.',
      color: 'red',
    });
  }

  function handleDeleteClick() {
    modals.openConfirmModal({
      title: 'Delete Poster Image',
      children: (
        <Text size="sm" c="dimmed">
          This will permanently remove the poster and thumbnail images.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deletePoster.mutateAsync();
          notifications.show({
            title: 'Poster deleted',
            message: 'The poster image has been removed.',
            color: 'green',
          });
        } catch (err) {
          notifications.show({
            title: 'Delete failed',
            message: err instanceof Error ? err.message : 'Failed to delete poster.',
            color: 'red',
          });
        }
      },
    });
  }

  // Determine which visual state to show
  const showDropzone = !hasPoster && !isProcessing && !clientPreviewUrl;
  const showClientPreview = clientPreviewUrl != null;
  const showProcessing = isProcessing && !clientPreviewUrl;
  const showPoster = hasPoster && !isProcessing && !clientPreviewUrl;

  return (
    <Stack gap="md" maw={640}>
      <Text fw={600} size="lg">Poster Image</Text>

      {/* State A: Empty dropzone (no poster, not processing) */}
      {showDropzone && (
        <Dropzone
          onDrop={handleDropzoneDrop}
          onReject={handleDropzoneReject}
          accept={POSTER_ACCEPTED_MIME_TYPES as unknown as string[]}
          maxSize={POSTER_MAX_FILE_SIZE}
          maxFiles={1}
          disabled={anyMutationPending}
        >
          <AspectRatio ratio={16 / 9}>
            <Stack align="center" justify="center" gap="xs">
              <IconPhoto size={48} stroke={1.5} color="var(--mantine-color-gray-5)" />
              <Text size="md" inline>
                Drop poster image here or click to browse
              </Text>
              <Text size="sm" c="dimmed" inline>
                JPG, PNG, WebP, HEIC/HEIF — Max 20MB — Recommended 1280 x 720
              </Text>
            </Stack>
          </AspectRatio>
        </Dropzone>
      )}

      {/* State B: Client-side preview during upload */}
      {showClientPreview && (
        <Box pos="relative">
          <AspectRatio ratio={16 / 9} bg="gray.1" style={{ borderRadius: 'var(--mantine-radius-sm)' }}>
            <Image
              src={clientPreviewUrl}
              alt="Uploading poster"
              radius="sm"
              fit="contain"
            />
          </AspectRatio>
          <Box
            pos="absolute"
            top={0}
            left={0}
            w="100%"
            h="100%"
            bg="rgba(255,255,255,0.7)"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--mantine-radius-sm)',
            }}
          >
            <Stack align="center" gap="xs">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">Uploading...</Text>
            </Stack>
          </Box>
        </Box>
      )}

      {/* State C: Processing indicator with progress */}
      {showProcessing && (
        <AspectRatio ratio={16 / 9} bg="gray.1" style={{ borderRadius: 'var(--mantine-radius-sm)' }}>
          <Stack align="center" justify="center" gap="xs">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              {posterStatus?.phase === 'ingesting' ? 'Uploading...' : 'Processing...'}
            </Text>
            {posterStatus && posterStatus.progress > 0 && posterStatus.progress < 100 && (
              <Progress value={posterStatus.progress} size="sm" w={200} />
            )}
          </Stack>
        </AspectRatio>
      )}

      {/* State D: Poster image display */}
      {showPoster && posterUrl && (
        <Stack gap="xs">
          <AspectRatio ratio={16 / 9}>
            <Image
              src={posterUrl}
              alt="Poster"
              radius="sm"
              fit="contain"
            />
          </AspectRatio>
          {posterFile.width && posterFile.height && (
            <Text size="sm" c="dimmed">
              {posterFile.width} x {posterFile.height}
              {posterFile.bytes > 0 && ` — ${MediaUtils.formatFileSize(posterFile.bytes)}`}
              {posterFile.format && ` — ${posterFile.format.toUpperCase()}`}
            </Text>
          )}
        </Stack>
      )}

      {/* Action buttons */}
      <Group gap="sm">
        <FileButton
          onChange={(file) => file && handleUploadFile(file)}
          accept={POSTER_ACCEPTED_MIME_TYPES.join(',')}
        >
          {(props) => (
            <Button
              {...props}
              leftSection={<IconUpload size={16} />}
              disabled={isProcessing || anyMutationPending}
            >
              {hasPoster ? 'Replace Image' : 'Upload Image'}
            </Button>
          )}
        </FileButton>

        <Button
          variant="light"
          leftSection={<IconCamera size={16} />}
          onClick={openCaptureForm}
          disabled={
            isProcessing ||
            anyMutationPending ||
            video.media.mediaStatusType !== MediaStatusType.Ready
          }
        >
          Capture Frame
        </Button>

        {hasPoster && (
          <Button
            variant="subtle"
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={handleDeleteClick}
            disabled={isProcessing || anyMutationPending}
          >
            Delete
          </Button>
        )}
      </Group>

      {/* Capture frame modal */}
      <Modal
        opened={captureOpened}
        onClose={closeCaptureForm}
        title="Capture Frame from Video"
        size="lg"
      >
        <CaptureFrameForm
          video={video}
          onCapture={(file) => handleUploadFile(file)}
          onClose={closeCaptureForm}
        />
      </Modal>
    </Stack>
  );
}
